/**
 * ERP CRUD modal tab rules:
 * ≤8 fields  → single panel, no tabs
 * 9–17 fields → 2 tabs
 * ≥18 fields → 2–3 tabs
 */

export function countErpModalFields(formSections) {
  return (formSections || []).reduce((total, section) => total + (section.fields?.length || 0), 0);
}

function taggedFields(formSections) {
  return (formSections || []).flatMap((section) =>
    (section.fields || []).map((field, index) => ({
      field,
      section,
      order: index,
    })),
  );
}

function chunkTaggedFields(tagged, chunkCount) {
  if (!tagged.length || chunkCount < 1) return [];

  const size = Math.ceil(tagged.length / chunkCount);
  const chunks = [];

  for (let i = 0; i < tagged.length; i += size) {
    chunks.push(tagged.slice(i, i + size));
  }

  return chunks.filter((chunk) => chunk.length > 0);
}

function sectionFromChunk(chunk, index) {
  const firstSection = chunk[0]?.section;
  const baseId = firstSection?.id || 'general';

  return {
    id: chunk.length === 1 && index === 0 ? baseId : `${baseId}_${index + 1}`,
    titleKey: firstSection?.titleKey || 'section_general',
    icon: firstSection?.icon,
    fields: chunk.map((item) => item.field),
  };
}

function mergeSectionsToCount(formSections, targetCount) {
  const tagged = taggedFields(formSections);
  return chunkTaggedFields(tagged, targetCount).map(sectionFromChunk);
}

function chooseTabCount(total, sectionCount) {
  if (total <= 8) return 0;
  if (total <= 17) return 2;
  if (sectionCount >= 3 && total >= 24) return 3;
  return total >= 27 ? 3 : 2;
}

/**
 * @param {Array<{ id?: string, titleKey?: string, icon?: import('react').ComponentType, fields?: Array }>} formSections
 * @returns {{ sections: Array, showTabs: boolean, fieldCount: number }}
 */
export function resolveErpModalFormSections(formSections) {
  const sections = Array.isArray(formSections) ? formSections.filter((s) => s?.fields?.length) : [];
  const fieldCount = countErpModalFields(sections);

  if (fieldCount === 0) {
    return {
      sections: [{ id: 'general', titleKey: sections[0]?.titleKey || 'section_general', fields: [] }],
      showTabs: false,
      fieldCount: 0,
    };
  }

  if (fieldCount <= 8) {
    return {
      sections: [{
        id: sections[0]?.id || 'general',
        titleKey: sections[0]?.titleKey || 'section_general',
        icon: sections[0]?.icon,
        fields: sections.flatMap((section) => section.fields || []),
      }],
      showTabs: false,
      fieldCount,
    };
  }

  const tabCount = chooseTabCount(fieldCount, sections.length);

  if (sections.length === tabCount) {
    return { sections, showTabs: true, fieldCount };
  }

  return {
    sections: mergeSectionsToCount(sections, tabCount),
    showTabs: true,
    fieldCount,
  };
}
